import React from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { MediaProp } from "types";
import { canonicalUrl, robotsContent } from "lib/seo";
import { socialImage, openGraphType } from "lib/social-image";

interface SEOType {
  data?: {
    Title?: string;
    MetaTitle?: string;
    MetaDescription?: string;
    MetaKeywords?: string;
    og_image?: MediaProp;
    og_url?: string;
    og_type?: string;
    og_title?: string;
    og_description?: string;
    twitter_title?: string;
    twitter_description?: string;
    twitter_image?: MediaProp;
    twitter_card?: string;
    IndexNoFollow?: string;
    Canonical?: string;
    RobotTXT?: string;
    HrefLang?: { hreflang?: string; href?: string }[];
  } | null;
  title?: string;
  description?: string;
}

const SEO: React.FC<SEOType> = ({ data, title: fallbackTitle, description: fallbackDescription }) => {
  const router = useRouter();
  const seo = data || {};
  const title = seo.Title || seo.MetaTitle || fallbackTitle;
  const description = seo.MetaDescription || fallbackDescription;
  const canonical = canonicalUrl({
    asPath: router.asPath,
    locale: router.locale,
    locales: router.locales ? [...router.locales] : undefined,
    explicit: seo.Canonical,
  });
  const robots = robotsContent(router.asPath, [seo.RobotTXT, seo.IndexNoFollow]);
  const ogImage = socialImage(seo.og_image, seo.og_title || title);
  const twitterImage = seo.twitter_image?.data ? socialImage(seo.twitter_image, seo.twitter_title || title) : ogImage;

  return (
    <Head>
      {title && <title key="title">{title}</title>}
      {description && <meta key="description" name="description" content={description} />}
      {seo.MetaKeywords && <meta key="keywords" name="keywords" content={seo.MetaKeywords} />}
      {canonical && <link key="canonical" rel="canonical" href={canonical} />}
      {canonical && <meta key="og:url" property="og:url" content={canonical} />}
      <meta key="og:site_name" property="og:site_name" content="DigiFist" />
      <meta key="og:type" property="og:type" content={openGraphType(seo.og_type)} />
      {(seo.og_title || title) && <meta key="og:title" property="og:title" content={seo.og_title || title} />}
      {(seo.og_description || description) && <meta key="og:description" property="og:description" content={seo.og_description || description} />}
      <meta key="og:image" property="og:image" content={ogImage.url} />
      <meta key="og:image:secure_url" property="og:image:secure_url" content={ogImage.url} />
      <meta key="og:image:type" property="og:image:type" content={ogImage.type} />
      {ogImage.width && <meta key="og:image:width" property="og:image:width" content={String(ogImage.width)} />}
      {ogImage.height && <meta key="og:image:height" property="og:image:height" content={String(ogImage.height)} />}
      <meta key="og:image:alt" property="og:image:alt" content={ogImage.alt} />
      {(seo.twitter_title || title) && <meta key="twitter:title" name="twitter:title" content={seo.twitter_title || title} />}
      {(seo.twitter_description || description) && <meta key="twitter:description" name="twitter:description" content={seo.twitter_description || description} />}
      <meta key="twitter:image" name="twitter:image" content={twitterImage.url} />
      <meta key="twitter:image:alt" name="twitter:image:alt" content={twitterImage.alt} />
      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      {robots && <meta key="robots" name="robots" content={robots} />}
    </Head>
  );
};

export default SEO;
